import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, LoadingState, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';

type EntityConfig = {
    title: string;
    apiPath: string;
    listPath: string;
    fields: Array<{ key: string; label: string }>;
    itemsKey?: string;
    itemColumns?: Array<{ key: string; label: string }>;
};

const CONFIGS: Record<string, EntityConfig> = {
    products: {
        title: 'Produto',
        apiPath: '/products',
        listPath: '/products',
        fields: [
            { key: 'code', label: 'Código' },
            { key: 'name', label: 'Nome' },
            { key: 'supplier', label: 'Fornecedor' },
            { key: 'category', label: 'Categoria' },
            { key: 'unit_price', label: 'Preço unitário' },
            { key: 'stock_items_count', label: 'Itens em estoque' },
        ],
    },
    orders: {
        title: 'Pedido',
        apiPath: '/orders',
        listPath: '/orders',
        fields: [
            { key: 'code', label: 'Código' },
            { key: 'status', label: 'Status' },
            { key: 'total_brl', label: 'Total BRL' },
            { key: 'total_usd', label: 'Total USD' },
        ],
        itemsKey: 'items',
        itemColumns: [
            { key: 'product_name', label: 'Produto' },
            { key: 'quantity', label: 'Qtd' },
            { key: 'total_brl', label: 'Total BRL' },
        ],
    },
    clients: {
        title: 'Cliente',
        apiPath: '/clients',
        listPath: '/clients',
        fields: [
            { key: 'name', label: 'Nome fantasia' },
            { key: 'legal_name', label: 'Razão social' },
            { key: 'cnpj', label: 'CNPJ' },
            { key: 'email', label: 'E-mail' },
            { key: 'phone', label: 'Telefone' },
            { key: 'city', label: 'Cidade' },
            { key: 'state', label: 'UF' },
            { key: 'address', label: 'Endereço' },
            { key: 'neighborhood', label: 'Bairro' },
            { key: 'zip_code', label: 'CEP' },
            { key: 'cnae', label: 'CNAE' },
            { key: 'active', label: 'Ativo' },
            { key: 'units_count', label: 'Unidades vinculadas' },
            { key: 'shipments_count', label: 'Embarques' },
        ],
    },
    users: {
        title: 'Usuário',
        apiPath: '/users',
        listPath: '/users',
        fields: [
            { key: 'name', label: 'Nome' },
            { key: 'email', label: 'E-mail' },
            { key: 'phone', label: 'Telefone' },
            { key: 'department', label: 'Setor' },
            { key: 'job_title', label: 'Cargo' },
            { key: 'locale', label: 'Idioma' },
            { key: 'timezone', label: 'Fuso horário' },
            { key: 'bio', label: 'Bio' },
        ],
    },
    'ploomes-attachments': {
        title: 'Documento Ploomes',
        apiPath: '/ploomes-attachments',
        listPath: '/ploomes-attachments',
        fields: [
            { key: 'display_name', label: 'Nome' },
            { key: 'file_name', label: 'Arquivo original' },
            { key: 'category_label', label: 'Categoria' },
            { key: 'is_receipt', label: 'Comprovante' },
            { key: 'is_validated', label: 'Validado' },
            { key: 'readable_size', label: 'Tamanho' },
            { key: 'content_type', label: 'Tipo' },
            { key: 'access_url', label: 'URL' },
            { key: 'ploomes_created_at', label: 'Criado em' },
            { key: 'notes', label: 'Observações' },
        ],
    },
    'ploomes-orders': {
        title: 'Venda Ploomes',
        apiPath: '/ploomes-orders',
        listPath: '/ploomes-orders',
        fields: [
            { key: 'order_number', label: 'Número' },
            { key: 'date', label: 'Data' },
            { key: 'contact_name', label: 'Cliente' },
            { key: 'person_name', label: 'Contato' },
            { key: 'amount', label: 'Valor' },
            { key: 'discount', label: 'Desconto' },
            { key: 'owner_name', label: 'Responsável' },
            { key: 'products_quantity_total', label: 'Itens (qtd total)' },
            { key: 'description', label: 'Descrição' },
            { key: 'internal_comments', label: 'Comentários internos' },
            { key: 'document_url', label: 'PDF' },
        ],
        itemsKey: 'products',
        itemColumns: [
            { key: 'product_name', label: 'Produto' },
            { key: 'quantity', label: 'Qtd' },
            { key: 'unit_price', label: 'Preço' },
            { key: 'total', label: 'Total' },
        ],
    },
};

export default function EntityDetailPage({ entityKey }: { entityKey: string }) {
    const { id } = useParams<{ id: string }>();
    const config = CONFIGS[entityKey];

    const { data, isLoading, error } = useQuery({
        queryKey: [entityKey, id],
        enabled: Boolean(config && id),
        queryFn: async () => {
            const { data: body } = await api.get(`${config!.apiPath}/${id}`);
            return body.data as Record<string, unknown>;
        },
    });

    if (!config) {
        return <p className="text-sm text-red-600">Entidade não configurada.</p>;
    }

    if (isLoading) {
        return <LoadingState label={`Carregando ${config.title.toLowerCase()}…`} />;
    }

    if (error || !data) {
        return <p className="text-sm text-red-600">Não foi possível carregar os dados.</p>;
    }

    const client = data.client as { name?: string } | undefined;
    const items = (config.itemsKey ? (data[config.itemsKey] as Array<Record<string, unknown>>) : []) ?? [];

    return (
        <div className="space-y-6">
            <PageHeader
                title={String(data.name ?? data.code ?? `${config.title} #${id}`)}
                breadcrumb={[
                    { name: 'Dashboard', href: '/' },
                    { name: config.title === 'Produto' ? 'Produtos' : 'Pedidos', href: config.listPath },
                    { name: String(data.code ?? id) },
                ]}
            >
                {entityKey === 'orders' && data.pdf_url && (
                    <a href={String(data.pdf_url)} target="_blank" rel="noreferrer">
                        <Badge color="primary">PDF</Badge>
                    </a>
                )}
                <Link to={config.listPath} className="text-sm text-amber-600 hover:underline">
                    Voltar à lista
                </Link>
            </PageHeader>

            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <dl className="grid gap-4 sm:grid-cols-2">
                    {config.fields.map((f) => (
                        <div key={f.key}>
                            <dt className="text-xs font-medium uppercase text-gray-500">{f.label}</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                                {f.key === 'suspended' || typeof data[f.key] === 'boolean'
                                    ? data[f.key]
                                        ? 'Sim'
                                        : 'Não'
                                    : String(data[f.key] ?? '—')}
                            </dd>
                        </div>
                    ))}
                    {client?.name && (
                        <div>
                            <dt className="text-xs font-medium uppercase text-gray-500">Cliente</dt>
                            <dd className="mt-1 text-sm text-gray-900">{client.name}</dd>
                        </div>
                    )}
                </dl>
            </div>

            {config.itemColumns && items.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                            <tr>
                                {config.itemColumns.map((c) => (
                                    <th key={c.key} className="px-4 py-3 text-left">
                                        {c.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map((row, idx) => (
                                <tr key={idx}>
                                    {config.itemColumns!.map((c) => (
                                        <td key={c.key} className="px-4 py-3">
                                            {String(row[c.key] ?? '—')}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
